import axios from 'axios'
import {getCertificateExpiryStatus} from '../../core/certificateExpiry'
import type {DashboardCertificate, DashboardFinding, QhseDashboardData} from './types'
import type {QHSENearMissReportDto} from '../../core/_models'

export function monthlyCounts(dates: (string | null | undefined)[], now = new Date()) {
  return Array.from({length: 6}, (_, index) => {
    const month = new Date(now.getFullYear(), now.getMonth() - 5 + index, 1)
    const key = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`
    return {label: month.toLocaleDateString('en-GB', {month: 'short', year: '2-digit'}),
      count: dates.filter(date => date?.startsWith(key)).length}
  })
}

export function certificateExpiryBuckets(certificates: DashboardCertificate[], now = new Date()) {
  const counts = [{label: 'Expired', count: 0}, {label: 'Due within 1 month', count: 0},
    {label: 'Due in 1–3 months', count: 0}]
  certificates.forEach(certificate => {
    const expiry = getCertificateExpiryStatus(certificate.dateOfExpiry, now)
    if (expiry.label === 'Expired') counts[0].count++
    else if (expiry.status === 'EXPIRED_OR_DUE_SOON') counts[1].count++
    else if (expiry.status === 'EXPIRING_WITHIN_3_MONTHS') counts[2].count++
  })
  return counts
}

export function chartNearMisses(reports: QHSENearMissReportDto[], now = new Date()) {
  const firstMonth = new Date(now.getFullYear(), now.getMonth() - 5, 1)
  const key = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
  return reports.filter(report => {
    const month = (report.dateOfOccurrence || report.dateIssued || report.createdAt || '').slice(0, 7)
    return report.occurrenceTypes?.some(type => type === 'NEAR_MISS' || type === 'HIGH_SEVERITY_NEAR_MISS') &&
      month >= key(firstMonth) && month <= key(now)
  })
}

export function kpiRecords(data: QhseDashboardData) {
  const year = String(new Date().getFullYear())
  return {
    openDefects: data.defects.filter(d => ['SAVE', 'SUBMIT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED'].includes(d.status ?? '')),
    nearMissesYTD: data.nearMisses.filter(n => n.occurrenceTypes?.some(t => t === 'NEAR_MISS' || t === 'HIGH_SEVERITY_NEAR_MISS') &&
      (n.dateOfOccurrence || n.dateIssued || n.createdAt || '').startsWith(year)),
    openAudits: data.audits.filter(a => a.isFinalized === false),
    openWorkPermits: data.workPermits.filter(w => w.status === 'SAVE' || w.status === 'SUBMIT'),
    pendingInspections: data.inspections.filter(i => i.isFinalized === false),
    expiringCerts: [...data.vesselCertificates, ...data.officeCertificates].filter(c => needsRenewal(c)),
  }
}

export function rowsFromResponse<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data
  if (data && Array.isArray((data as {content?: unknown}).content)) return (data as {content: T[]}).content
  throw new Error('Unexpected dashboard response')
}

/** Audits/inspections can return either a full list or a pageable response. */
export async function allPages<T>(fetchPage: (page: number) => Promise<unknown>): Promise<T[]> {
  const first = await fetchPage(0)
  const rows = [...rowsFromResponse<T>(first)]
  const pages = (first as {totalPages?: number})?.totalPages ?? 1
  for (let page = 1; page < pages; page++) rows.push(...rowsFromResponse<T>(await fetchPage(page)))
  return rows
}

export async function loadCertificates(scope: 'Vessel' | 'Office', vesselIds: number[]): Promise<DashboardCertificate[]> {
  if (!vesselIds.length) return []
  const path = scope === 'Vessel' ? 'certificates' : 'office-certificates'
  const {data} = await axios.post(`${process.env.REACT_APP_API_URL}/qhse/${path}/vessel`, vesselIds)
  return rowsFromResponse<DashboardCertificate>(data).map(row => ({...row, scope}))
}

export const needsRenewal = (certificate: DashboardCertificate, today = new Date()) => {
  const status = getCertificateExpiryStatus(certificate.dateOfExpiry, today).status
  return status === 'EXPIRED_OR_DUE_SOON' || status === 'EXPIRING_WITHIN_3_MONTHS'
}
export const isOpenFinding = (finding: DashboardFinding) => finding.active !== false &&
  finding.status !== 'SATISFACTORY' &&
  (finding.workflowStatus ? finding.workflowStatus !== 'APPROVED' : finding.isClosed !== true)

export function newestFirst<T extends {id?: number}>(rows: T[], date: (row: T) => string | null | undefined): T[] {
  const time = (row: T) => Date.parse(date(row) ?? '') || 0
  return [...rows].sort((a, b) => time(b) - time(a) || Number(b.id ?? 0) - Number(a.id ?? 0))
}
